import { AppSessionState } from '@lib/store/app-session.state';

/**
 * Validates if a session is still valid based on token expiration
 * @param session - The session to validate
 * @returns boolean indicating if session is valid
 */
export function isValidSession(session: AppSessionState): boolean {
    if (!session?.accessToken) {
        return false;
    }

    try {
        const tokenParts = session.accessToken.split('.');
        if (tokenParts.length !== 3) {
            return false;
        }

        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        const bufferTime = 5 * 60;
        return payload.exp > currentTime + bufferTime;
    } catch (error) {
        console.error('Failed to validate session:', error);
        return false;
    }
}

/**
 * Checks if token needs refresh (within 10 minutes of expiration)
 * @param session - The session to check
 * @returns boolean indicating if token should be refreshed
 */
export function shouldRefreshToken(session: AppSessionState): boolean {
    if (!session?.accessToken) {
        return false;
    }

    try {
        const tokenParts = session.accessToken.split('.');
        if (tokenParts.length !== 3) {
            return false;
        }

        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        const refreshThreshold = 10 * 60;
        return payload.exp <= currentTime + refreshThreshold;
    } catch (error) {
        console.error('Failed to check if token should be refreshed:', error);
        return true;
    }
}

/**
 * Gets the time remaining before token expiration (in seconds)
 * @param session - The session to check
 * @returns number of seconds until expiration, or 0 if expired/invalid
 */
export function getTokenTimeRemaining(session: AppSessionState): number {
    if (!session?.accessToken) {
        return 0;
    }

    try {
        const tokenParts = session.accessToken.split('.');
        if (tokenParts.length !== 3) {
            return 0;
        }

        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Math.floor(Date.now() / 1000);

        return Math.max(0, payload.exp - currentTime);
    } catch (error) {
        console.warn('Failed to get token time remaining:', error);
        return 0;
    }
}
